/**
 * Voice-delivery prompt templates (display/speech split) — TS twin of
 * `backend/app/services/voice_delivery.py`. The backend module is the
 * canonical implementation (server-canonical chat path); this twin exists
 * for the avatar path, which composes its prompt in the browser.
 * `shared/voice-delivery-templates.json` is the parity corpus asserted by
 * BOTH test suites so the two cannot silently drift.
 *
 * Spec: docs/superpowers/specs/2026-07-28-voice-track-display-speech-split-design.md
 */

export type SpeechMode = 'verbatim' | 'gloss' | 'full' | 'adaptive'

const CORE =
  'Your reply is delivered on two channels at once: it is shown on screen ' +
  'and simultaneously spoken aloud. Begin your reply with the spoken ' +
  'version, wrapped exactly in <voice> and </voice> tags. Inside the tags ' +
  'write natural conversational speech: complete sentences only — no ' +
  'lists, no bullet points, no headings, no citations, no markdown, no ' +
  'URLs. Never mention the tags or the two channels. After the closing ' +
  '</voice> tag, write the complete answer for the screen in your normal ' +
  'format, with structure, lists, and citations as usual.'

const MODE_CLAUSES: Partial<Record<SpeechMode, string>> = {
  gloss:
    'Keep the spoken version to one to three sentences: give the key ' +
    'takeaway conversationally and point the listener to the screen for ' +
    'the rest (for example: the full list is on your screen).',
  full:
    'The spoken version must cover all of the same information as the ' +
    'on-screen answer, rewritten as flowing natural speech — connect the ' +
    'points with spoken transitions instead of list structure, and do ' +
    'not drop content.',
  adaptive:
    'If the answer is short and simple, speak it in full. If it is long ' +
    'or structured, keep the spoken version to one to three sentences: ' +
    'give the key takeaway and point the listener to the screen for the ' +
    'details.',
}

/** Rendered instruction for `mode`, or null for verbatim/unknown/absent. */
export function voiceDeliveryText(mode: SpeechMode | undefined | null): string | null {
  if (!mode) return null
  const clause = MODE_CLAUSES[mode]
  if (!clause) return null
  return CORE + '\n\n' + clause
}
